import { forwardRef } from 'react';
import styled from 'styled-components';
import { MessageCircle, X } from 'lucide-react';

/**
 * The launcher's fixed footprint. MainLayout derives its content bottom
 * clearance from this so page-bottom content (pagination chevrons in
 * particular, #537) can never sit underneath the bubble — keep in sync with
 * the Launcher styles below. Carried over verbatim from the interim
 * SupportContactPopover this widget replaces.
 */
export const SUPPORT_LAUNCHER_SIZE_PX = 58;

const Launcher = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${SUPPORT_LAUNCHER_SIZE_PX}px;
  height: ${SUPPORT_LAUNCHER_SIZE_PX}px;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ theme }) => theme.colors.primary.dark};
  color: ${({ theme }) => theme.colors.text.white};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  cursor: pointer;
  transition: transform ${({ theme }) => theme.transitions.fast};

  &:hover {
    transform: scale(1.05);
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary.sky};
    outline-offset: 2px;
  }

  svg {
    width: 26px;
    height: 26px;
  }
`;

const Badge = styled.span`
  position: absolute;
  top: -2px;
  right: -2px;
  min-width: 20px;
  height: 20px;
  padding: 0 4px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ theme }) => theme.colors.status.error.text};
  color: ${({ theme }) => theme.colors.text.white};
  font-size: 11px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  line-height: 20px;
  text-align: center;
  border: 2px solid ${({ theme }) => theme.colors.backgrounds.white};
`;

export interface SupportLauncherProps {
  isOpen: boolean;
  unreadCount: number;
  onClick: () => void;
}

// forwardRef so SupportWidget can return focus here when the panel closes
// (Escape, close button) — otherwise focus falls to <body> and keyboard/
// screen-reader users lose their place.
export const SupportLauncher = forwardRef<HTMLButtonElement, SupportLauncherProps>(
  ({ isOpen, unreadCount, onClick }, ref) => {
    return (
      <Launcher
        ref={ref}
        type="button"
        onClick={onClick}
        aria-label={isOpen ? 'Close support' : 'Open support'}
        aria-expanded={isOpen}
      >
        {isOpen ? <X aria-hidden="true" /> : <MessageCircle aria-hidden="true" />}
        {!isOpen && unreadCount > 0 && (
          <Badge aria-label={`${unreadCount} unread support messages`}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </Badge>
        )}
      </Launcher>
    );
  },
);
SupportLauncher.displayName = 'SupportLauncher';

export default SupportLauncher;
